import { useState, useMemo, useRef } from 'react';
import { Box, ClickAwayListener, Typography, useTheme } from '@mui/material';

import MenuItemContent from './MenuItemContent';
import IconToggle from './IconToggle';
import {
  StyledFormControl,
  StyledInputLabel,
  StyledButtonWrapper,
  StyledButton,
  StyledMenuItem,
  StyledPaper,
  menuItemContentStyles,
} from './styles';

export default function GenericDropdown({
  items,
  selectedItem,
  onSelect,
  formik,
  name,
  buttonLabel = 'Seleccioná una opción',
  id = 'generic-dropdown-button',
  labelIcon,
  isPrimaryBorder,
  iconWidth = 28,
  noBorder = false,
  hasArrow = true,
  mainColorInHover = false,
  hasError = false,
  helperText = '',
  menuItemHeight = 40,
  formControlHeight = 45,
  centerLabel = false,
  formControlHeightMobile = 45,
  autoWidth = false,
  isOpen,
  setIsOpen,
}) {
  const [hover, setHover] = useState(false);

  const [internalOpen, setInternalOpen] = useState(false);

  const open = isOpen === undefined ? internalOpen : isOpen;
  const setOpen = setIsOpen || setInternalOpen;

  const ignoreClickAway = useRef(false);
  const theme = useTheme();

  const effectiveSelectedItem = useMemo(() => {
    if (selectedItem?.fragments?.[0]?.text) return selectedItem;
    return {
      id: null,
      icon: labelIcon,
      fragments: [{ text: buttonLabel, bold: false }],
    };
  }, [selectedItem, labelIcon, buttonLabel]);

  const isFloating = useMemo(
    () =>
      !!selectedItem?.fragments &&
      selectedItem.fragments[0].text &&
      selectedItem.fragments[0].text !== buttonLabel,
    [selectedItem, buttonLabel]
  );

  const selectedLabel = useMemo(
    () => (
      <Box
        id="box-menu-item-content"
        sx={{
          ...menuItemContentStyles(theme, menuItemHeight, mainColorInHover),
        }}
      >
        <MenuItemContent
          icon={effectiveSelectedItem.icon}
          fragments={effectiveSelectedItem.fragments}
          iconWidth={iconWidth}
        />
      </Box>
    ),
    [effectiveSelectedItem, iconWidth, theme]
  );

  const handleClick = () => {
    ignoreClickAway.current = true;
    setOpen(!open);
  };

  const handleClickAway = () => {
    if (ignoreClickAway.current) {
      ignoreClickAway.current = false;
      return;
    }
    setOpen(false);
  };

  const handleClose = (item = null) => {
    setOpen(false);
    if (!item) return;

    // Si pasamos formik y name, hacemos setFieldValue automáticamente
    if (formik && name) {
      formik.setFieldValue(name, item.id?.toString() || '');
    }

    if (onSelect) {
      onSelect(item);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <StyledFormControl
        fullWidth
        variant="outlined"
        noBorder={noBorder}
        isPrimaryBorder={isPrimaryBorder}
        formControlHeight={formControlHeight}
        formControlHeightMobile={formControlHeightMobile}
      >
        <StyledInputLabel
          shrink={Boolean(isFloating)}
          isFloating={isFloating}
          isPrimaryBorder={isPrimaryBorder}
        >
          {isFloating ? buttonLabel : ''}
        </StyledInputLabel>

        <StyledButtonWrapper
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          noBorder={noBorder}
          isPrimaryBorder={isPrimaryBorder}
        >
          <StyledButton
            id={`button-${id}-${buttonLabel}${name ?? ''}`}
            onClick={e => {
              e.stopPropagation();
              handleClick();
            }}
            disableRipple
            variant="text"
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                textAlign: centerLabel ? 'center' : 'left',
              }}
            >
              {selectedLabel}
            </Box>
            {hasArrow && (
              <IconToggle
                open={open}
                isPrimaryBorder={isPrimaryBorder}
                hover={hover}
              />
            )}
          </StyledButton>
        </StyledButtonWrapper>
      </StyledFormControl>

      {open && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <StyledPaper autoWidth={autoWidth}>
            {items?.map((item, i) => (
              <StyledMenuItem
                key={item.id ?? i}
                onClick={() => handleClose(item)}
                onMouseEnter={item?.onMouseEnter}
                onMouseLeave={item?.onMouseLeave}
                mainColorInHover={mainColorInHover}
                menuItemHeight={menuItemHeight}
              >
                <MenuItemContent
                  icon={item.icon}
                  mobileWithIcon={item.mobileWithIcon}
                  fragments={item.fragments}
                  iconWidth={iconWidth}
                />
              </StyledMenuItem>
            ))}
          </StyledPaper>
        </ClickAwayListener>
      )}

      {hasError && helperText && (
        <Typography
          color="error"
          sx={{ mt: '3px', ml: '14px', fontSize: '12px', lineHeight: '1.66' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
